// src/pages/Shipments.jsx
import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { formatDateTime } from '../utils/formatDate';
import Pagination from '../components/Pagination';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';

const Shipments = () => {
  const [shipmentData, setShipmentData] = useState({
    shipment_number: '',     
    carrier_id: '',            
    origin: 'Miami, Florida',  
    destination: '',        
    departs_on: '',             
    arrive_on: '',            
    vessel_number: '',       
    auto_update_status: 1,    
    manifest_weight: '',      
    freight_type_id: 'Air',     
  });

  const [metaData, setMetaData] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 0,
  });

  const [shipments, setShipment] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentShipmentId, setCurrentShipmentId] = useState(null);
  const [selectedShipmentId, setSelectedShipmentId] = useState(null); // new state for selection
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiUrl = process.env.REACT_APP_BASE_URL;

  const fetchShipment = async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/shipments?page=${currentPage}`);
      if (!response.ok) {
        throw new Error('Failed to fetch shipments');
      }
      const data = await response.json();
      setShipment(data['shipments']);
      setMetaData(data['meta']);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCarriers = async () => {
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/carriers`);
      if (!response.ok) {
        throw new Error('Failed to fetch carriers');
      }
      const data = await response.json();
      setCarriers(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDestinations = async () => {
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/locations`);
      if (!response.ok) {
        throw new Error('Failed to fetch destinations');
      }
      const data = await response.json();
      setDestinations(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchShipment();
    fetchCarriers();
    fetchDestinations();
    setLoading(false);
  }, [currentPage]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (editMode) {
        const response = await fetch(`${apiUrl}/api/shipments/${currentShipmentId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(shipmentData),
        });
        const responseData = await response.json();
        if (!response.ok) {
          throw new Error(responseData.error || 'Failed to update shipment');
        }
        toast.success('Shipment updated successfully!');
      } else {
        const response = await fetch(`${apiUrl}/api/shipments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(shipmentData),
        });
        const responseData = await response.json();
        if (!response.ok) {
          throw new Error(responseData.error || 'Failed to create shipment');
        }
        toast.success('Shipment created successfully!');
      }
      fetchShipment();
      handleModalClose();
    } catch (error) {
      toast.error(error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    const shipmentToEdit = shipments.find((shpmt) => shpmt.id === id);
    if (shipmentToEdit) {
      setShipmentData(shipmentToEdit);
      setCurrentShipmentId(id);
      setEditMode(true);
      setModalOpen(true);
    }
  };

  const handleDeleteModal = (id) => {
    const shipmentToDelete = shipments.find((shpmt) => shpmt.id === id);
    if (shipmentToDelete) {
      setShipmentData(shipmentToDelete);
      setCurrentShipmentId(id);
      setModalDeleteOpen(true);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${apiUrl}/api/shipments/${id}`, {
        method: 'DELETE',
      });
      fetchShipment();
      setModalDeleteOpen(false);
      // Clear selection if the deleted shipment was selected
      if (selectedShipmentId === id) {
        setSelectedShipmentId(null);
      }
    } catch (error) {
      console.error('Error deleting shipment:', error);
      setError('Error deleting shipment. Please try again.');
      setModalDeleteOpen(false);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditMode(false);
    setCurrentShipmentId(null);
    setShipmentData({
      shipment_number: '',     
      carrier_id: '',            
      origin: 'Miami, Florida',  
      destination: '',        
      departs_on: '',             
      arrive_on: '',            
      vessel_number: '',       
      auto_update_status: 1,    
      manifest_weight: '',      
      freight_type_id: 'Air',     
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setShipmentData({ ...shipmentData, [name]: value });
  };

  // New functions to operate on the selected shipment
  const handleEditSelected = () => {
    if (!selectedShipmentId) {
      toast.error('Please select a shipment to edit.');
      return;
    }
    handleEdit(selectedShipmentId);
  };

  const handleDeleteSelected = () => {
    if (!selectedShipmentId) {
      toast.error('Please select a shipment to delete.');
      return;
    }
    handleDeleteModal(selectedShipmentId);
  };

  return (
    <Layout>
      {loading ? <Spinner /> : (
        <main className="p-4 bg-base-100 shadow-xl w-4/5 lg:w-full h-full">
          <section className="flex justify-between items-center mb-4">
            <h2 className="text-3xl font-semibold">Shipments</h2>
            <div className="flex space-x-2">
              <button 
                className="btn btn-secondary" 
                onClick={handleEditSelected} 
                disabled={!selectedShipmentId}
              >
                Edit Selected
              </button>
              <button 
                className="btn btn-error text-white" 
                onClick={handleDeleteSelected} 
                disabled={!selectedShipmentId}
              >
                Delete Selected
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => setModalOpen(true)}
              >
                Add Shipment
              </button>
            </div>
          </section>
          <section className="mb-4">
            <table className="table-auto w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-2 py-2">Select</th>
                  <th className="border px-2 sm:px-4 py-2 text-left font-semibold">Shipment Number</th>
                  <th className="border px-2 sm:px-4 py-2 text-left font-semibold">Carrier</th>
                  <th className="border px-2 sm:px-4 py-2 text-left font-semibold">Origin</th>
                  <th className="border px-2 sm:px-4 py-2 text-left font-semibold">Destination</th>
                  <th className="border px-2 sm:px-4 py-2 text-left font-semibold">Departs On</th>
                  <th className="border px-2 sm:px-4 py-2 text-left font-semibold">Arrives On</th>
                  <th className="border px-2 sm:px-4 py-2 text-left font-semibold">Vessel Number</th>
                  <th className="border px-2 sm:px-4 py-2 text-left font-semibold">Auto Update Status</th>
                  <th className="border px-2 sm:px-4 py-2 text-left font-semibold">Manifest Weight</th>
                  <th className="border px-2 sm:px-4 py-2 text-left font-semibold">Freight Type</th>
                </tr>
              </thead>
              <tbody>
                {shipments.map((shpmt) => (
                  <tr 
                    key={shpmt.id} 
                    className={`md:table-row block md:border-none border border-gray-300 ${
                      selectedShipmentId === shpmt.id ? 'bg-blue-100' : ''
                    }`}
                  >
                    <td className="border px-2 py-2 text-center">
                      <input
                        type="radio"
                        name="selectedShipment"
                        checked={selectedShipmentId === shpmt.id}
                        onChange={() => setSelectedShipmentId(shpmt.id)}
                      />
                    </td>
                    <td className="md:border border-gray-300 px-4 py-2" data-label="Shipment Number">
                      {shpmt.shipment_number}
                    </td>
                    <td className="md:border border-gray-300 px-4 py-2" data-label="Carrier">
                      {shpmt.carrier_id}
                    </td>
                    <td className="md:border border-gray-300 px-4 py-2" data-label="Origin">
                      {shpmt.origin}
                    </td>
                    <td className="md:border border-gray-300 px-4 py-2" data-label="Destination">
                      {shpmt.destination}
                    </td>
                    <td className="md:border border-gray-300 px-4 py-2" data-label="Departs On">
                      {formatDateTime(shpmt.departs_on)}
                    </td>
                    <td className="md:border border-gray-300 px-4 py-2" data-label="Arrives On">
                      {formatDateTime(shpmt.arrive_on)}
                    </td>
                    <td className="md:border border-gray-300 px-4 py-2" data-label="Vessel Number">
                      {shpmt.vessel_number}
                    </td>
                    <td className="md:border border-gray-300 px-4 py-2" data-label="Auto Update Status">
                      {shpmt.auto_update_status ? "True" : "False"}
                    </td>
                    <td className="md:border border-gray-300 px-4 py-2" data-label="Manifest Weight">
                      {shpmt.manifest_weight}
                    </td>
                    <td className="md:border border-gray-300 px-4 py-2" data-label="Freight Type">
                      {shpmt.freight_type_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <Pagination 
            currentPage={metaData.currentPage}
            totalPages={metaData.totalPages}   
            onPageChange={(page) => setCurrentPage(page)}
          />
        </main>
      )}

      {modalOpen && (
        <dialog className="modal modal-open bg-gray-50">
          <form onSubmit={handleSubmit} className="modal-box space-y-4">
            <h1 className="text-3xl text-center mb-6">
              {editMode ? 'Edit Shipment' : 'Add Shipment'}
            </h1>
            <div className="flex gap-4">
              <div className="form-control">
                <label htmlFor="shipment_number" className="label">
                  <span className="label-text">Shipment Number</span>
                </label>
                <input
                  type="text"
                  id="shipment_number"
                  name="shipment_number"
                  value={shipmentData.shipment_number}
                  onChange={handleInputChange}
                  className="input input-bordered w-full"
                  required
                />
              </div>
              <div className="form-control">
                <label htmlFor="carrier_id" className="label">
                  <span className="label-text">Carrier</span>
                </label>
                <select
                  id="carrier_id"
                  name="carrier_id"
                  value={shipmentData.carrier_id}
                  onChange={handleInputChange}
                  className="select select-bordered w-full"
                  required
                >
                  <option value="" disabled>Select a carrier</option>
                  {carriers.map((carrier, index) => (
                    <option key={index} value={carrier.name}>
                      {carrier.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="form-control">
                <label htmlFor="origin" className="label">
                  <span className="label-text">Origin</span>
                </label>
                <input
                  type="text"
                  id="origin"
                  name="origin"
                  value={shipmentData.origin}
                  onChange={handleInputChange}
                  className="input input-bordered w-full"
                />
              </div>
              <div className="form-control">
                <label htmlFor="destination" className="label">
                  <span className="label-text">Destination</span>
                </label>
                <select
                  id="destination"
                  name="destination"
                  value={shipmentData.destination}
                  onChange={handleInputChange}
                  className="select select-bordered w-full"
                  required
                >
                  <option value="" disabled>Select a destination</option>
                  {destinations.map((destination, index) => (
                    <option key={index} value={destination.name}>
                      {destination.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="form-control">
                <label htmlFor="departs_on" className="label">
                  <span className="label-text">Departs On</span>
                </label>
                <input
                  type="date"
                  id="departs_on"
                  name="departs_on"
                  value={shipmentData.departs_on}
                  onChange={handleInputChange}
                  className="input input-bordered w-full"
                />
              </div>
              <div className="form-control">
                <label htmlFor="arrive_on" className="label">
                  <span className="label-text">Arrives On</span>
                </label>
                <input
                  type="date"
                  id="arrive_on"
                  name="arrive_on"
                  value={shipmentData.arrive_on}
                  onChange={handleInputChange}
                  className="input input-bordered w-full"
                />
              </div>
            </div>
            <div className="form-control">
              <label htmlFor="vessel_number" className="label">
                <span className="label-text">Vessel Number</span>
              </label>
              <input
                type="text"
                id="vessel_number"
                name="vessel_number"
                value={shipmentData.vessel_number}
                onChange={handleInputChange}
                className="input input-bordered w-full"
              />
            </div>
            <div className="form-control">
              <label htmlFor="waybill_number" className="label">
                <span className="label-text">Waybill Number</span>
              </label>
              <input
                type="text"
                id="waybill_number"
                name="waybill_number"
                value={shipmentData.waybill_number || ''}
                onChange={handleInputChange}
                className="input input-bordered w-full"
              />
            </div>
            {/* <div className="form-control">
              <label htmlFor="auto_update_status" className="label">
                <span className="label-text">Auto Update Status</span>
              </label>
              <select
                id="auto_update_status"
                name="auto_update_status"
                value={shipmentData.auto_update_status}
                onChange={handleInputChange}
                className="select select-bordered w-full"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              <span className="label-text-alt">
                <span className="text-gray-500">Auto-update the status of shipment</span>
              </span>
            </div> */}
            <div className="form-control">
              <label htmlFor="manifest_weight" className="label">
                <span className="label-text">Manifest Weight (Kgs)</span>
              </label>
              <input
                type="number"
                id="manifest_weight"
                name="manifest_weight"
                value={shipmentData.manifest_weight}
                onChange={handleInputChange}
                className="input input-bordered w-full"
              />
            </div>
            <div className="form-control">
              <label htmlFor="freight_type_id" className="label">
                <span className="label-text">Freight Type</span>
              </label>
              <select
                id="freight_type_id"
                name="freight_type_id"
                value={shipmentData.freight_type_id}
                onChange={handleInputChange}
                className="select select-bordered w-full"
              >
                <option value="Air">Air</option>
                <option value="Sea">Sea</option>
                <option value="Land">Land</option>
              </select>
            </div>
            <div className="flex gap-4 justify-center">
              <button type="button" className="btn btn-secondary" onClick={handleModalClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Saving..." : "Save Shipment"}
              </button>
            </div>
          </form>
        </dialog>
      )}

      {modalDeleteOpen && (
        <dialog id="delete_modal" className="modal modal-open">
          <div className="modal-box">
            <h3 className="text-lg font-bold">
              Delete {shipmentData.shipment_number}
            </h3>
            <p className="py-4">
              Are you sure you want to delete {shipmentData.shipment_number}?
            </p>
            <div className="flex space-x-4">
              <button className="btn bg-error text-white" onClick={() => handleDelete(shipmentData.id)}>
                Delete
              </button>
              <button className="btn" onClick={() => setModalDeleteOpen(false)}>
                Close
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop"></form>
        </dialog>
      )}
    </Layout>
  );
};

export default Shipments;
